import { useState } from 'react';
import { usePwaInstalar } from '../../lib/pwaInstall';

/**
 * Convites de PWA da plataforma (B2B) — banner no TOPO, com MEMÓRIA PERSISTENTE
 * por dispositivo. Toda a lógica (PROATIVA no mobile, standalone/appinstalled/
 * dispensa/iOS/foco mobile) vive em lib/pwaInstall.ts (compartilhada com o app
 * do comprador). Aqui só a aparência + os textos da marca AMA Capital.
 *
 * Atualização de versão NÃO tem banner: o SW é 'autoUpdate' e recarrega sozinho.
 */

// Chave VERSIONADA: para reoferecer numa mudança major, bump para _v2.
const DISMISS_KEY = 'pwa_prompt_dismissed_v1';

function IconeInstalar() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden="true">
      <rect x="4" y="2" width="16" height="20" rx="3" stroke="currentColor" strokeWidth="1.6" />
      <path d="M12 7v7m0 0l-2.5-2.5M12 14l2.5-2.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M10.5 18.5h3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function Banner({
  children,
  onClose,
  testid,
}: {
  children: React.ReactNode;
  onClose: () => void;
  testid: string;
}) {
  return (
    <div
      data-testid={testid}
      role="dialog"
      aria-live="polite"
      className="fixed inset-x-3 top-[calc(3.5rem+0.75rem)] z-30 flex justify-center md:top-4"
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
    >
      <div
        className="flex w-full max-w-md items-start gap-3 rounded-2xl border border-line bg-surface px-4 py-3.5 shadow-lg"
        style={{ boxShadow: 'var(--shadow-sm)' }}
      >
        <span
          className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full"
          style={{ background: 'var(--accent-soft)', color: 'var(--accent-bright)' }}
          aria-hidden="true"
        >
          <IconeInstalar />
        </span>
        <div className="min-w-0 flex-1">{children}</div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Dispensar"
          data-testid={`${testid}-fechar`}
          className="-mr-1 -mt-1 shrink-0 rounded-full p-1.5 text-ink-3 transition-colors hover:bg-bg-warm hover:text-ink"
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </button>
      </div>
    </div>
  );
}

export function PwaPrompts() {
  const { modo, instalarNativo, dispensar } = usePwaInstalar(DISMISS_KEY);
  const [mostrarComo, setMostrarComo] = useState(false);

  if (modo === 'ios') {
    return (
      <Banner testid="pwa-ios" onClose={dispensar}>
        <div className="text-sm font-semibold text-ink">Instale o app da AMA Capital</div>
        <div className="mt-0.5 text-xs text-ink-3">
          Acesso rápido pela tela inicial: toque em <strong>Compartilhar</strong> e depois em{' '}
          <strong>Adicionar à Tela de Início</strong>.
        </div>
      </Banner>
    );
  }

  if (modo === 'android') {
    return (
      <Banner testid="pwa-instalar" onClose={dispensar}>
        <div className="text-sm font-semibold text-ink">Instale o app da AMA Capital</div>
        {mostrarComo ? (
          <div className="mt-0.5 text-xs text-ink-3" data-testid="pwa-instalar-como">
            Abra o menu do navegador <strong>(⋮)</strong> e toque em <strong>Instalar app</strong> (ou{' '}
            <strong>Adicionar à tela inicial</strong>).
          </div>
        ) : (
          <>
            <div className="mt-0.5 text-xs text-ink-3">Acesso rápido pela tela inicial, sem passar pelo navegador.</div>
            <button
              type="button"
              onClick={() => (instalarNativo ? void instalarNativo() : setMostrarComo(true))}
              data-testid="pwa-instalar-acao"
              className="mt-2.5 rounded-full bg-accent px-3.5 py-1.5 text-xs font-semibold text-white"
            >
              Instalar
            </button>
          </>
        )}
      </Banner>
    );
  }

  return null;
}
